import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.payments_api.payment_types;

const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'payments_api',
  group: 'payment_types',
  defaultTtl: '10m',
  hashLargeSegments: true,
  emitMeta: true,
});

// /GetAllPaymentTypesByLocalId

export const getAllPaymentTypesByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetAllPaymentTypesByLocalId/${localId}`)
  );
}

export const getAllPaymentTypes = async () => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetAllPaymentTypes`)
  );
}

export const addPaymentTypeToLocal = async (localId: number, paymentId: number) => {
  const res = await retryWithBackoff(() =>
    api.post(`/AddPaymentTypeToLocal/${localId}/${paymentId}`, {})
  );
  await cachedApi.clear();
  return res;
}

export const deletePaymentTypeFromLocal = async (localId: number, paymentId: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeletePaymentTypeFromLocal/${localId}/${paymentId}`)
  );
  await cachedApi.clear();
  return res;
}

/**
 * 
# ============================================================================
# CUSTOM PAYMENT TYPES ENDPOINTS
# ============================================================================

custom_payment_type_model = ns.model('CustomPaymentType', {
    'custom_payment_type_id': fields.Integer(readonly=True, description='The custom payment type unique identifier'),
    'local_id': fields.Integer(required=True, description='The local identifier'),
    'custom_payment_name': fields.String(required=True, description='The custom payment type name'),
    'active': fields.Boolean(description='Whether the custom payment type is active'),
    'created_at': fields.DateTime(readonly=True, description='Creation timestamp'),
    'updated_at': fields.DateTime(readonly=True, description='Last update timestamp'),
})

create_custom_payment_type_parser = reqparse.RequestParser()
create_custom_payment_type_parser.add_argument('custom_payment_name', type=str, required=True, help='The custom payment type name is required')

@ns.route('/CreateCustomPaymentType/<int:local_id>')
@ns.param('local_id', 'The local identifier')
class CreateCustomPaymentType(Resource):
    @ns.doc('create_custom_payment_type')
    @ns.expect(create_custom_payment_type_parser)
    @ns.marshal_with(custom_payment_type_model)
    @jwt_required()
    def post(self, local_id):
        '''Create a custom payment type for a specific local'''
        args = create_custom_payment_type_parser.parse_args()
        custom_payment_name = args['custom_payment_name'].strip()
        
        # Validate local exists
        local = Local.query.get_or_404(local_id)
        
        # Check for duplicate name within the same local
        existing = LocalCustomPaymentType.query.filter_by(
            local_id=local_id,
            custom_payment_name=custom_payment_name
        ).first()
        
        if existing:
            return {'message': f'Custom payment type "{custom_payment_name}" already exists for this local'}, 400
        
        # Create custom payment type
        custom_payment_type = LocalCustomPaymentType(
            local_id=local_id,
            custom_payment_name=custom_payment_name,
            active=True
        )
        
        db.session.add(custom_payment_type)
        db.session.commit()
        
        return custom_payment_type, 201

@ns.route('/GetCustomPaymentTypesByLocalId/<int:local_id>')
@ns.param('local_id', 'The local identifier')
class GetCustomPaymentTypesByLocalId(Resource):
    @ns.doc('get_custom_payment_types_by_local_id')
    @ns.marshal_list_with(custom_payment_type_model)
    @jwt_required()
    def get(self, local_id):
        '''Get all custom payment types for a specific local (active and inactive)'''
        # Validate local exists
        local = Local.query.get_or_404(local_id)
        
        # Get all custom payment types for this local
        custom_payment_types = LocalCustomPaymentType.query.filter_by(local_id=local_id).all()
        
        return custom_payment_types, 200
 */

export const createCustomPaymentType = async (local_id: number, custom_payment_name: string) => {
  const res = await retryWithBackoff(() =>
    api.post(`/CreateCustomPaymentType/${local_id}`, {
      custom_payment_name
    })
  );
  await cachedApi.clear();
  return res;
}

export const getCustomPaymentTypesByLocalId = async (local_id: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetCustomPaymentTypesByLocalId/${local_id}`)
  );
}